import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../common/Button";
import { failed, success } from "../../../common/Toastify";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useSchedulingOrg } from "../../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useServiceCategoriesQuery } from "../../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import {
  approveAppointment,
  cancelAppointment,
  getAppointment,
  launchAppointmentEncounter,
  rejectAppointment,
} from "../../../api/Scheduling/appointments";
import { schedulingErrorMessage } from "../../../api/Scheduling/errors";
import { formatInZone, formatRange, instantToLocalDate } from "../time";
import { timeZoneLabel } from "../timeZones";
import { useSchedulingTimeZone } from "../../../../hooks/useSchedulingTimeZone";
import { loadPatientName, loadPractitionerName } from "../names";
import { appointmentStatusLabel, subjectPatientRef } from "./statusLabels";
import { appointmentChannel, communicationMethodLabel, openEncounterSession } from "./launchAppointmentSession";
import BookingModal from "../booking/BookingModal";
import { appointmentHasLinkedEncounter, loadAppointmentEncounter } from "./appointmentEncounter";
import { handlePatientClick } from "../../../../utils/NavigationSupport";
import "../Scheduling.css";
import AppointmentReferralRecovery from "./AppointmentReferralRecovery";

function openAppTab(event, path) {
  event.preventDefault();
  window.open(`${window.location.origin}${path}`, "_blank");
}

const AppointmentDetailModal = ({
  appointment: initial,
  organizationRef: viewOrganizationRef,
  onHide,
  onChanged,
  readOnly = false,
  isProviderAppointment = false,
  timezoneName,
  scheduleTimezoneName,
}) => {
  const organizationRef = initial?.organizationRef || viewOrganizationRef;
  const navigate = useNavigate();
  const { getConfirmation } = useConfirmDialog();
  const { practitionerRef, user } = useSchedulingOrg();
  const displayZone = useSchedulingTimeZone(user);
  const { data: categories = [] } = useServiceCategoriesQuery({ organizationRef, active: undefined });
  const [appointment, setAppointment] = useState(initial);
  const [patientName, setPatientName] = useState("");
  const [practitionerNames, setPractitionerNames] = useState([]);
  const [rescheduleShow, setRescheduleShow] = useState(false);
  const [encounter, setEncounter] = useState(null);
  const [encounterLoading, setEncounterLoading] = useState(true);
  const [encounterError, setEncounterError] = useState("");
  const [encounterRetry, setEncounterRetry] = useState(0);
  const [busy, setBusy] = useState("");
  const actionInFlight = useRef(false);
  const zone = timezoneName || displayZone.timeZone;

  const refresh = async () => {
    const res = await getAppointment({
      organizationRef,
      appointmentId: initial.appointmentId,
    });
    setAppointment(res?.appointment || res);
  };

  useEffect(() => {
    setAppointment(initial);
  }, [initial]);

  useEffect(() => {
    let active = true;
    setPatientName("");
    setPractitionerNames([]);
    const patientRef = subjectPatientRef(initial);
    if (patientRef) loadPatientName(patientRef).then((name) => { if (active) setPatientName(name); });
    Promise.all((appointment?.practitionerRefs || []).map(loadPractitionerName)).then((names) => {
      if (active) setPractitionerNames(names);
    });
    return () => { active = false; };
  }, [initial, appointment?.practitionerRefs]);

  useEffect(() => {
    let active = true;
    setEncounter(null);
    setEncounterError("");
    setEncounterLoading(true);
    loadAppointmentEncounter(appointment, organizationRef).then((record) => {
      if (active) setEncounter(record);
    }).catch(() => {
      if (active) setEncounterError("Could not load visit details.");
    }).finally(() => { if (active) setEncounterLoading(false); });
    return () => { active = false; };
  }, [appointment, organizationRef, encounterRetry]);

  const categoryName =
    categories.find((category) => category.serviceCategoryId === appointment?.serviceCategoryId)?.name ||
    "Appointment type unavailable";

  const runAction = async (fn, label) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setBusy(label);
    try {
      await fn({
        organizationRef,
        appointmentId: appointment.appointmentId,
        version: appointment.version,
      });
      success(label);
      await refresh();
      onChanged?.();
    } catch (error) {
      failed(schedulingErrorMessage(error));
    } finally {
      actionInFlight.current = false;
      setBusy("");
    }
  };

  const handleCancel = async (blockOriginal = false) => {
    if (appointmentHasLinkedEncounter(appointment, encounter)) return;
    const confirmed = await getConfirmation({
      title: blockOriginal ? "Block and cancel appointment?" : "Cancel appointment?",
      entryName: "this appointment",
      actionBtnTitle: blockOriginal ? "Block and cancel" : "Cancel Appointment",
    });
    if (!confirmed) return;
    await runAction(
      (args) => cancelAppointment({ ...args, ...(blockOriginal ? { blockOriginal: true } : {}) }),
      blockOriginal ? "Appointment cancelled and original time blocked" : "Appointment cancelled",
    );
  };

  const openEncounter = (record) => openEncounterSession({
    record,
    appointment,
    practitionerRef,
    patientName,
    navigate,
    readOnly,
    isProviderAppointment,
  });

  const handleLaunch = async () => {
    if (actionInFlight.current || appointmentHasLinkedEncounter(appointment, encounter)) return;
    actionInFlight.current = true;
    setBusy("launch");
    try {
      if (!appointment.bookingContext) throw new Error("This older appointment has no source encounter reference. Its existing encounter must be linked before it can be opened.");
      const record = await launchAppointmentEncounter({ appointment, organizationRef, practitionerRef });
      setEncounter(record);
      await refresh();
      onChanged?.();
      await openEncounter(record);
    } catch (error) {
      failed(schedulingErrorMessage(error));
      await refresh().catch(() => {});
    } finally {
      actionInFlight.current = false;
      setBusy("");
    }
  };

  if (!appointment) return null;
  const patientRef = subjectPatientRef(appointment);
  const hasLinkedEncounter = appointmentHasLinkedEncounter(appointment, encounter);
  const actionsLocked = !!busy || encounterLoading || !!encounterError || hasLinkedEncounter;

  return (
    <>
    <Modal show={!rescheduleShow} onHide={busy ? undefined : onHide} centered className="custom-dialog scheduling-dialog appointment-detail-dialog" aria-labelledby="appointment-detail-title">
      <Modal.Header closeButton={!busy}>
        <div className="appointment-detail-heading">
        <Modal.Title id="appointment-detail-title">
          {patientName && patientRef ? (
            <a className="appointment-patient-link" href={`/app/patients-details?patientId=${encodeURIComponent(patientRef)}`} onClick={(event) => {
              event.preventDefault();
              handlePatientClick({ id: patientRef });
            }}>
              {patientName}
            </a>
          ) : patientName || "Appointment"}
        </Modal.Title>
          <span className={`scheduling-status ${appointment.status}`}>
            {appointmentStatusLabel(appointment.status)}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body>
        {appointment.bookingContext?.internalReferral && !readOnly && user?.permissions?.includes("Create Service Referrals") &&
          <AppointmentReferralRecovery appointment={appointment} />}
        <div className="appointment-detail-time">
          <h3>{formatInZone(appointment.startsAt, zone, "ddd, MMM D, YYYY")}</h3>
          <p>{formatInZone(appointment.startsAt, zone, "h:mm A")} – {formatInZone(appointment.endsAt, zone,
            instantToLocalDate(appointment.startsAt, zone) === instantToLocalDate(appointment.endsAt, zone) ? "h:mm A" : "ddd, MMM D · h:mm A")}</p>
          <span>{timeZoneLabel(zone)} time</span>
          {scheduleTimezoneName && scheduleTimezoneName !== zone && <small className="schedule-zone-secondary">
            {formatRange(appointment.startsAt, appointment.endsAt, scheduleTimezoneName)} · {timeZoneLabel(scheduleTimezoneName)}
          </small>}
        </div>
        <dl className="appointment-detail-fields">
          {appointment.bookingContext?.sourceEncounterId && <div><dt>Source encounter ID</dt><dd><a className="appointment-encounter-link" href={`/app/encounter-details?encounterId=${encodeURIComponent(appointment.bookingContext.sourceEncounterId)}`} onClick={(event) => openAppTab(event, `/app/encounter-details?encounterId=${encodeURIComponent(appointment.bookingContext.sourceEncounterId)}`)}>{appointment.bookingContext.sourceEncounterId}</a></dd></div>}
          {hasLinkedEncounter && <div><dt>Encounter ID</dt><dd><a className="appointment-encounter-link" href={`/app/encounter-details?encounterId=${encodeURIComponent(encounter?.id || appointment.encounterId)}`} onClick={(event) => openAppTab(event, `/app/encounter-details?encounterId=${encodeURIComponent(encounter?.id || appointment.encounterId)}`)}>{encounter?.id || appointment.encounterId}</a></dd></div>}
          <div><dt>Appointment type</dt><dd>{categoryName}</dd></div>
          <div><dt>Reason for visit</dt><dd>{appointment.bookingContext?.reason || encounter?.intakeQuestions?.intakeReason || "Not recorded"}</dd></div>
          <div><dt>Practitioner{practitionerNames.length > 1 ? "s" : ""}</dt><dd>{practitionerNames.join(", ") || "Loading…"}</dd></div>
          <div><dt>Communication method</dt><dd>{communicationMethodLabel(appointmentChannel(appointment, encounter))}</dd></div>
        </dl>
        {encounterError && <p className="schedule-error" role="alert">{encounterError} <button type="button" className="schedule-text-button" onClick={() => setEncounterRetry((value) => value + 1)}>Retry</button></p>}
      </Modal.Body>
        {!readOnly ? (
          <Modal.Footer className="schedule-dialog-footer appointment-detail-actions">
            {!isProviderAppointment && appointment.practitionerRefs?.includes(practitionerRef) && (appointment.status === "booked" || encounter?.id) && (
              <Button onClick={handleLaunch} disabled={actionsLocked || appointment.status !== "booked"} isLoading={busy === "launch"}>Launch</Button>
            )}
            {appointment.status === "proposed" ? (
              <>
                <Button disabled={!!busy} onClick={() => runAction(approveAppointment, "Appointment accepted")}>Accept</Button>
                <Button disabled={!!busy} variant="secondary" onClick={() => runAction(rejectAppointment, "Appointment rejected")}>
                  Reject
                </Button>
              </>
            ) : null}
            {appointment.status === "booked" ? (
              <>
                <Button variant="secondary" disabled={actionsLocked} onClick={() => { if (!actionsLocked) setRescheduleShow(true); }}>Reschedule</Button>
                <Button className="appointment-cancel-action" disabled={actionsLocked} onClick={() => handleCancel(false)}>Cancel Appointment</Button>
                <Button variant="secondary" disabled={actionsLocked} onClick={() => handleCancel(true)}>Block and cancel</Button>
              </>
            ) : null}
            {appointment.status === "proposed" ? (
              <>
                <Button className="appointment-cancel-action" disabled={actionsLocked} onClick={() => handleCancel(false)}>
                  Cancel Appointment
                </Button>
                <Button variant="secondary" disabled={actionsLocked} onClick={() => handleCancel(true)}>Block and cancel</Button>
              </>
            ) : null}
          </Modal.Footer>
        ) : null}
    </Modal>
      {rescheduleShow ? (
        <BookingModal
          modalShow
          type="reschedule"
          onHide={() => setRescheduleShow(false)}
          appointment={appointment}
          patientId={subjectPatientRef(appointment)}
          patientName={patientName}
          orgId={organizationRef}
          appointmentEncounter={encounter}
          encounterLoading={encounterLoading}
          encounterError={encounterError}
          onEncounterReload={() => { refresh().catch(() => {}); setEncounterRetry((value) => value + 1); }}
          appointmentPractitionerNames={practitionerNames}
          onSuccess={() => {
            setRescheduleShow(false);
            refresh();
            onChanged?.();
          }}
        />
      ) : null}
    </>
  );
};

export default AppointmentDetailModal;
